/**
 * SmartDeepLink — intercepts mobile browser navigation to deep-linkable routes
 * and attempts to open the native app first.
 *
 * IMPORTANT: Only attempts deep linking for LOGGED IN users.
 * First-time / unknown visitors stay on the website to complete
 * the Stripe signup flow without interruption.
 *
 * If the app is installed → opens directly (e.g. transportmate://job/abc-123)
 * If not installed → the page loads normally and the MobileAppBanner shows
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isNative } from "@/lib/capacitor";
import { isMobileBrowser, APP_DEEP_LINK } from "@/lib/mobileAppBanner";

/** Routes that support deep linking into the native app */
const DEEP_LINKABLE_PREFIXES = [
  "/job/",
  "/resume-job/",
  "/invoice/",
  "/appraisal/",
];

export function SmartDeepLink() {
  const location = useLocation();
  const { status } = useAuth();
  const attemptedRef = useRef<string | null>(null);

  useEffect(() => {
    // Only run on mobile browsers
    if (isNative) return;
    if (!isMobileBrowser()) return;

    // Only deep-link for authenticated / returning users
    if (status !== "authed") return;

    const path = location.pathname;

    // Only deep-link for supported routes
    const isDeepLinkable = DEEP_LINKABLE_PREFIXES.some((p) => path.startsWith(p));
    if (!isDeepLinkable) return;

    // Don't re-attempt for the same path
    if (attemptedRef.current === path) return;
    attemptedRef.current = path;

    // Try to open the app — if it's not installed, the page just stays as-is
    const deepLink = `${APP_DEEP_LINK}${path.replace(/^\//, "")}`;
    window.location.href = deepLink;
  }, [location.pathname, status]);

  return null;
}

export default SmartDeepLink;
